import {delay, eventChannel} from 'redux-saga';
import {call, fork, put, select, take, takeEvery,} from 'redux-saga/effects';
import SockJS from 'sockjs-client';
import Stomp from 'stompjs';
import {
    DESTROY_CHAT,
    END_CONVERSATION_FOR_OPERATOR_REQUESTED,
    FORWARD_CONVERSATION_REQUESTED,
    INIT_CHAT_REQUESTED,
    LOAD_OLDER_MESSAGES_REQUESTED,
    SELECT_CONVERSATION_REQUESTED,
    SEND_CHAT_MESSAGE_REQUESTED, SET_MESSAGE_SEEN, USER_TYPING_STOP,
} from './actionTypes';
import {
    endConversationForOperator,
    fetchCompanyListConversationsForOwner,
    fetchConversationMessages,
    fetchConversationsWithMembers,
    fetchOperatorCompanyListConversations,
    fetchOperatorCompanyListConversationsForOwner,
    fetchOperatorConversationMessages,
    fetchOperatorListConversations,
    forwardConversationForOperator,
    getMessage as getMessageApi,
    makeConversationMessagesSeen,
    sendChatMessage as sendChatMessageApi,
    sendOperatorChatMessage as sendOperatorChatMessageApi,
    takeConversationForOperator,
    updateMessage as updateMessageApi,
} from './api';
import {
    clearSelectedConversation as clearSelectedConversationFromStore,
    deleteChatMessage,
    editChatMessage,
    endConversationFailed,
    endConversationSucceeded,
    forwardConversationFailed,
    forwardConversationSucceeded,
    handleNotSentMessage,
    initChatFailed,
    initChatSucceeded,
    insertChatMessage,
    insertConversation,
    loadOlderMessagesFailed,
    loadOlderMessagesSucceeded,
    removeConversation,
    selectConversationFailed,
    selectConversationSucceeded,
    sendChatMessageFailed,
    sendChatMessageSucceeded,
    setConversationsList,
    updateConversation, userTypingStart, userTypingStop,
} from './action';
import {addNotification} from '../notification/action';
import config from '../../config';

import {getChat, getCompany, getConversationsList, getSelectedConversationFromStore, getUser,} from '../sagaUtils';
import {convertToBackendDate, hasChatPermission} from '../../utils/General';
import {moveConversationToUnManagedList} from "../company/api";

export default function* watchChat() {
    yield takeEvery(INIT_CHAT_REQUESTED, initChat);
    yield takeEvery(SELECT_CONVERSATION_REQUESTED, selectConversation);
    yield takeEvery(SEND_CHAT_MESSAGE_REQUESTED, sendChatMessage);
    yield takeEvery(END_CONVERSATION_FOR_OPERATOR_REQUESTED, endConversationForOperatorF);
    yield takeEvery(FORWARD_CONVERSATION_REQUESTED, forwardConversation);
    yield takeEvery(LOAD_OLDER_MESSAGES_REQUESTED, loadOlderMessages);
    yield takeEvery(DESTROY_CHAT, destroyChat);
}

export function* initChat() {
    try {
        const chat = yield select(getChat);
        if (chat.synced) {
            yield put(initChatSucceeded());
            return;
        }

        const user = yield select(getUser);
        const company = yield select(getCompany);
        const { members } = company;
        const { subCompanyIds } = user;

        const memberConversations = yield call(fetchConversationsWithMembers);
        for (const conversation of memberConversations) {
            yield call(fillConversationMemberInfo, conversation, members);
        }

        let conversationsList = [...memberConversations];

        if (hasChatPermission(user)) {
            let incomingConversations = [];
            let operatorCompanyListConversations = [];

            if (user.authorities.includes(config.rolesMap.owner) || user.authorities.includes(config.rolesMap.admin)) {
                incomingConversations = yield call(fetchCompanyListConversationsForOwner, config.conversationStatuses.unManaged, user.id);
                operatorCompanyListConversations = yield call(fetchOperatorCompanyListConversationsForOwner);
            } else if (subCompanyIds.length !== 0) {
                incomingConversations = yield call(fetchOperatorListConversations, subCompanyIds, config.conversationStatuses.unManaged, user.platformId);
                operatorCompanyListConversations = yield call(fetchOperatorCompanyListConversations, subCompanyIds);
            }

            conversationsList = [
                ...conversationsList,
                ...incomingConversations,
                ...operatorCompanyListConversations,
            ];
        }

        yield put(setConversationsList(conversationsList));

        const socketChannel = yield call(initSocketChannel, user, company);
        Socket.setSocketChannel(socketChannel);
        yield fork(function* () {
            try {
                yield call(initSocketMessageHandling, socketChannel);
            } catch (err) {
                console.error('Socket handling error:', err);
                yield put(initChatFailed());
                yield put(addNotification('serviceUpdate', config.notificationLevels.info));
                yield delay(2000);
                yield call(initChat); // retry
            }
        });
        yield put(initChatSucceeded());

    } catch (err) {
        console.error('initChat error:', err);

        yield put(initChatFailed());
        yield put(addNotification('serviceUpdate', config.notificationLevels.info));
        yield delay(2000);
        yield call(initChat);
    }
}

function* destroyChat() {
    try {
        yield put(setConversationsList([]));
        const socketChannel = Socket.getSocketChannel();
        if (socketChannel !== null) {
            socketChannel.close();
        }
    } catch (e) {
        console.log(e);
    }
}

const Socket = (function () {
    let socket = null;
    let socketChannel = null;

    return {
        getSocket: () => {
            if (socket === null) {
                socket = new SockJS(`${config.BACKEND_HOST}/ws`);
            }
            return socket;
        },
        getSocketChannel: () => socketChannel,
        setSocketChannel: (channel) => {
            socketChannel = channel;
        },
        closeSocket: () => {
            if (socket !== null) {
                socket.close();
            }
        },
        reInit: () => {
            socket = new SockJS(`${config.BACKEND_HOST}/ws`);
        },
    };
}());

const messagesTypes = {
    newConversation: 'newConversation',
    removeConversation: 'removeConversation',
    newUnManagedConversationMessage: 'newUnManagedConversationMessage',
    newMessage: 'newMessage',
    seenMessage: 'seenMessage',
    typingStart: 'typingStart',
    typingStop: 'typingStop',
    newOperatorContact: 'newOperatorContact', // unused
    conversationForward: 'conversationForward',
    operatorConnected: 'operatorConnected', // unused
    operatorDisconnected: 'operatorDisconnected', // unused
};

function initSocketChannel(user, company) {
    return new Promise((res, rej) => {
        const channel = eventChannel((emitter) => {
            Socket.reInit();
            const socket = Socket.getSocket();
            const stompClient = Stomp.over(socket);
            stompClient.debug = null;

            const companyIds = company.subs.map(sub => sub.id);
            companyIds.push(company.id);

            const connect = () => {
                stompClient.connect({}, () => {
                    stompClient.subscribe(`/chat/${user.platformId}/conversations/messages`, (payload) => {
                        emitter({ type: messagesTypes.newMessage, message: payload });
                    });
                    stompClient.subscribe(`/chat/${user.platformId}/conversations/new`, (payload) => {
                        emitter({ type: messagesTypes.newConversation, message: payload });
                    });
                    stompClient.subscribe(`/chat/${user.platformId}/conversations/remove`, (payload) => {
                        emitter({ type: messagesTypes.removeConversation, message: payload });
                    });
                    stompClient.subscribe(`/chat/${user.platformId}/conversations/forward`, (payload) => {
                        emitter({ type: messagesTypes.conversationForward, message: payload });
                    });
                    stompClient.subscribe(`/chat/${user.platformId}/conversations/message-seen`, (payload) => {
                        emitter({ type: messagesTypes.seenMessage, message: payload });
                    });
                    stompClient.subscribe(`/chat/${user.platformId}/conversations/typing-start`, (payload) => {
                        emitter({ type: messagesTypes.typingStart, message: payload });
                    });
                    stompClient.subscribe(`/chat/${user.platformId}/conversations/typing-stop`, (payload) => {
                        emitter({ type: messagesTypes.typingStop, message: payload });
                    });

                    if (hasChatPermission(user)) {
                        companyIds.forEach((companyId) => {
                            stompClient.subscribe(`/chat/${companyId}/conversations/new`, (payload) => {
                                emitter({ type: messagesTypes.newConversation, message: payload });
                            });
                            stompClient.subscribe(`/chat/${companyId}/conversations/remove`, (payload) => {
                                emitter({ type: messagesTypes.removeConversation, message: payload });
                            });
                            stompClient.subscribe(`/chat/${companyId}/conversations/messages`, (payload) => {
                                emitter({ type: messagesTypes.newUnManagedConversationMessage, message: payload });
                            });
                            stompClient.subscribe(`/chat/${companyId}/conversations/forward`, (payload) => {
                                emitter({ type: messagesTypes.conversationForward, message: payload });
                            });
                            stompClient.subscribe(`/chat/${companyId}/conversations/message-seen`, (payload) => {
                                emitter({ type: messagesTypes.seenMessage, message: payload });
                            });
                            stompClient.subscribe(`/chat/${companyId}/conversations/typing-start`, (payload) => {
                                emitter({ type: messagesTypes.typingStart, message: payload });
                            });
                            stompClient.subscribe(`/chat/${companyId}/conversations/typing-stop`, (payload) => {
                                emitter({ type: messagesTypes.typingStop, message: payload });
                            });
                        });
                    }

                    res(channel);
                }, (error) => {
                    console.error("WebSocket connect error:", error);
                    emitter(new Error('WebSocket connection error. Reconnecting...'));

                    setTimeout(() => connect(), 3000);
                });
            };

            connect();

            return () => {
                if (stompClient.connected) {
                    stompClient.disconnect(() => {
                        console.log("WebSocket disconnected cleanly");
                    });
                }
            };
        });

    });
}

function* initSocketMessageHandling(channel) {
    while (true) {
        const event = yield take(channel);
        switch (event.type) {
            case messagesTypes.newConversation:
                yield call(handleNewConversation, event.message);
                break;
            case messagesTypes.removeConversation:
                yield call(handleRemoveConversation, event.message);
                break;
            case messagesTypes.conversationForward:
                yield call(handleConversationForward, event.message);
                break;
            case messagesTypes.newMessage:
                yield call(handleNewMessage, event.message);
                break;
            case messagesTypes.seenMessage:
                yield call(handleMessageSeen, event.message);
                break;
            case messagesTypes.typingStart:
                yield call(handleUserTypingStart, event.message);
                break;
            case messagesTypes.typingStop:
                yield call(handleUserTypingStop, event.message);
                break;
            case messagesTypes.newUnManagedConversationMessage:
                yield call(handleNewMessage, event.message);
                break;
            case messagesTypes.operatorConnected:
                yield call(handleOperatorConnected, event.message);
                break;
            case messagesTypes.operatorDisconnected:
                yield call(handleOperatorDisconnected, event.message);
                break;
            default:
                console.log('Unknown message');
        }
    }
}

function* handleNewConversation(message) {
    const conversation = JSON.parse(message.body);
    const user = yield select(getUser);

    initializeSoundSetting(conversation.id);

    if (isSoundEnabledForConversationAndUser(conversation.id, user)) {
        if (conversation && conversation.operatorPlatformId === user.id) { // ignore supervisor new chat sound
            playChatNotificationSound();
        }
    }

    try {
        if (!user.subCompanyIds.includes(conversation.companyId) && user.role === config.rolesMap.agent) {
            return
        }
        yield put(insertConversation(conversation));
        yield put(addNotification('newConversationReceived', config.notificationLevels.info));
    } catch (error) {
        console.error('Error handling new conversation:', error);
    }
}

function* handleUserTypingStart(socketMessage) {
    const conversation = JSON.parse(socketMessage.body);
    yield put(userTypingStart(conversation.id));
}

function* handleUserTypingStop(socketMessage) {
    const conversation = JSON.parse(socketMessage.body);
    yield put(userTypingStop(conversation.id));
}

function* handleConversationForward(message) {
    const conversation = JSON.parse(message.body);
    yield put(insertConversation(conversation));
    yield put(addNotification('newConversationReceivedForward', config.notificationLevels.info));
}

function* handleNewMessage(socketMessage) {
    const conversationAndMessage = JSON.parse(socketMessage.body);
    const {conversation, message} = conversationAndMessage;
    const user = yield select(getUser);

    if (!message.afk) {
        yield put(updateConversation(conversation.id, {
            afk: false
        }));
    }

    if (message.notSent) {
        yield put(handleNotSentMessage(message));
        return;
    }

    if (message.isDeleted) {
        yield put(deleteChatMessage(message));
        return
    }

    if (message.isEdited) {
        yield put(editChatMessage(message));
        return
    }

    initializeSoundSetting(conversation.id);

    const conversationList = yield select(getConversationsList);
    let conversationFromStore = conversationList.find(curConversation => curConversation.id === conversation.id);
    if (conversationFromStore === undefined || conversationFromStore == null) {
        yield put(insertConversation(conversation));
        conversationFromStore = conversation;
    }

    const chat = yield select(getChat);
    const {conversation: selectedConversation} = chat;

    const update = getConversationUpdateFromMessage(conversationFromStore, message, message.afk);

    if (isSoundEnabledForConversationAndUser(conversation.id, user)) {
        if (conversation && conversation.operator && conversation.operator.platformId === user.id) { // ignore supervisor messages sound
            playMessageNotificationSound();
        }
    }

    if (conversationFromStore.id === selectedConversation.id) {
        if (message.id !== null) { // if not null, message is not draft (sent)

            // make message seen by operator
            if (conversation
                &&
                conversation.operator
                &&
                user
                &&
                (conversation.operator.platformId === user.id)) { // check supervisor case
                yield fork(updateMessageApi, message.id, {seen: true});
            }
            //

            const apiMessage = yield call(getMessageApi, message.id);
            yield call(fillMessageContactInfo, apiMessage);
            if (!message.afk) { // don't add afk notification in chat (notification is a message)
                yield put(insertChatMessage(apiMessage));
            }
        } else {
            // handle draft messages, contact avatar, text, id
            message.id = message.text;
            message.from.platform = config.platformsMap.webchat;
            yield call(fillMessageContactInfo, message);
            if (!message.afk) {
                yield put(insertChatMessage(message));
            }
        }
        Object.assign(update, {unread: 0});
    }

    yield put(updateConversation(conversationFromStore.id, update));

    yield put({ // remove typing indicator
        type: USER_TYPING_STOP,
        payload: { conversationId: conversation.id },
    });
}

function* handleMessageSeen(socketMessage) {
    const message = JSON.parse(socketMessage.body);
    yield put({
        type: SET_MESSAGE_SEEN,
        payload: { messageId: message.id },
    });
}

function isSoundEnabledForConversationAndUser(conversationId, user) {
    const soundSetting = sessionStorage.getItem(`soundEnabled-${conversationId}`);
    const soundSettingForUser = sessionStorage.getItem(`soundEnabled-${user.id}`);
    return soundSetting === 'true' && soundSettingForUser === 'true';
}

function playMessageNotificationSound() {
    const audio = new Audio('/mp3/message-sound.mp3');
    audio.play().catch((err) => console.warn('Sound play failed', err));
}

function playChatNotificationSound() {
    const audio = new Audio('/mp3/new-chat.mp3');
    audio.play().catch((err) => console.warn('Sound play failed', err));
}

function initializeSoundSetting(conversationId) {
    if (sessionStorage.getItem(`soundEnabled-${conversationId}`) === null) {
        sessionStorage.setItem(`soundEnabled-${conversationId}`, 'true');
    }
}

function getConversationUpdateFromMessage(conversation, message, afk) {
    let unread;
    if (conversation.unread == null) {
        unread = 1;
    } else if (conversation.messageId !== message.id) {
        unread = conversation.unread + 1;
    }

    if (message.fromOperator) {
        return {
            type: message.type,
            text: message.text,
            date: message.date,
            attachmentId: message.attachmentWindow,
            unread,
        };
    } else {
        return {
            type: message.type,
            text: message.text,
            date: message.date,
            attachmentId: message.attachmentWindow,
            unread,
            afk: afk
        };
    }
}

function* handleRemoveConversation(message) {
    const {conversationId} = JSON.parse(message.body);
    yield put(removeConversation(conversationId));
    // yield put(addNotification('chatRemove', config.notificationLevels.info));
}

function* handleOperatorConnected(message) {
    yield console.log(message);
}

function* handleOperatorDisconnected(message) {
    yield console.log(message);
}

function* selectConversation(action) {
    try {
        const {conversation} = action.payload;
        const selectedConversation = yield select(getSelectedConversationFromStore);

        if (conversation.id === selectedConversation.id) {
            return;
        }

        const user = yield select(getUser);
        const company = yield select(getCompany);
        const {members} = company;


        let updatedConversation = {}
        if (conversation.status === config.conversationStatuses.unManaged) {
            updatedConversation = yield call(takeConversationForOperator, conversation.id, user.id);
        }

        if (updatedConversation === 'Operator chat limit') {
            yield put(addNotification('operatorChatLimit', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'Operator not available') {
            yield put(addNotification('operatorNotAvailable', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'This conversation is not visible to this operator') {
            yield put(addNotification('visibleError', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'Operator current chat limit') {
            yield put(addNotification('operatorCurrentChatLimit', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'Operator is not attached to this bot') {
            yield put(addNotification('operatorNotAttachedToBot', config.notificationLevels.error));
            return
        }
        //

        const {
            messages,
            totalNumber
        } = conversation.isOperatorConversation ? yield call(fetchOperatorConversationMessages, conversation.operatorConversationId) : yield call(fetchConversationMessages, conversation.id);

        for (const message of messages) {
            yield call(fillMessageContactInfo, message, members);
        }

        yield call(makeConversationMessagesSeen, conversation.id);

        yield put(updateConversation(conversation.id, Object.assign(updatedConversation, {unread: 0})));

        yield put(selectConversationSucceeded(conversation, messages, totalNumber));

    } catch (e) {
        console.log('can not select conversation', e);
        yield put(selectConversationFailed());
    }
}

function* sendChatMessage(action, conv) {
    try {
        const commentMessage = action.payload.commentMessage
        const {message: messageContent} = action.payload;
        const chat = yield select(getChat);
        const user = yield select(getUser);
        const conversation = conv ? conv : chat.conversation;


        const message = {
            platform: conversation.platform,
            chatId: conversation.chatId,
            companyId: conversation.companyId,
            date: convertToBackendDate(new Date()),
        };

        Object.assign(message, messageContent);

        let resultingMessage;
        if (conversation.platform === config.platformsMap.company) {
            resultingMessage = yield call(sendOperatorChatMessageApi, user.id, conversation.id, message);
        } else {
            resultingMessage = yield call(sendChatMessageApi, conversation.id, message, commentMessage);
        }
        yield call(fillMessageContactInfo, resultingMessage);

        yield put(insertChatMessage(resultingMessage));

        yield put(sendChatMessageSucceeded());

        yield put(updateConversation(conversation.id, {
            id: conversation.id,
            text: resultingMessage.text,
            date: resultingMessage.date,
            type: resultingMessage.type,
            attachmentId: resultingMessage.attachmentWindow,
            afk: false
        }));

    } catch (e) {
        yield put(sendChatMessageFailed());
    }
}

function* endConversationForOperatorF(data) {
    try {
        const chat = yield select(getChat);
        let conversation = chat.conversation;

        const silentMod = data.silentMod
        const conv = data.conv

        // end chat by right click case (ChatContact)
        if (conv && conv.id !== null) {
            conversation = conv;
        }
        //

        yield call(endConversationForOperator, conversation.id, silentMod);
        yield put(removeConversation(conversation.id));
        yield put(endConversationSucceeded(conversation));

    } catch (err) {
        console.error(err)
        yield put(endConversationFailed());
    }
}

function* forwardConversation(action) {
    try {
        const chat = yield select(getChat);
        let {conversation} = chat;
        const {operatorContact} = action.payload;
        const silentMod = action.silentMod
        const conv = action.conv

        // forward chat by right click case (ChatContact)
        if (conv && conv.id !== null) {
            conversation = conv;
        }
        //

        // move to unmanaged list case
        if (operatorContact === "-1") {
            yield call(moveConversationToUnManagedList, conversation.id, silentMod)
            yield put(clearSelectedConversationFromStore());
            return
        }
        //

        let updatedConversation;
        updatedConversation = yield call(forwardConversationForOperator, conversation.id, operatorContact.id, silentMod);

        if (updatedConversation === 'Operator chat limit') {
            yield put(addNotification('operatorChatLimit', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'Operator not available') {
            yield put(addNotification('operatorNotAvailable', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'This conversation is not visible to this operator') {
            yield put(addNotification('visibleError', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'Operator current chat limit') {
            yield put(addNotification('operatorCurrentChatLimit', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'Operator is not attached to this bot') {
            yield put(addNotification('operatorNotAttachedToBot', config.notificationLevels.error));
            return
        } else if (updatedConversation === 'The operator is already connected to this conversation') {
            yield put(addNotification('operatorAlreadyAttached', config.notificationLevels.info));
            return
        }

        yield put(clearSelectedConversationFromStore());
        yield put(forwardConversationSucceeded());
        yield put(addNotification('successToForwardConversation', config.notificationLevels.success));
    } catch (e) {
        console.log(e)
        yield put(addNotification('failedToForwardConversation', config.notificationLevels.error));
        yield put(forwardConversationFailed());
    }
}

function* fillConversationMemberInfo(conversation, members) {
    if (members === undefined) {
        const company = yield select(getCompany);
        // eslint-disable-next-line prefer-destructuring
        members = company.members;
    }

    const conversationOperator = members.find(operator => operator.id === conversation.userPlatformId);
    if (conversationOperator) {
        conversation.userFirstName = conversationOperator.firstName;
        conversation.userLastName = conversationOperator.lastName;
        conversation.userThumbUrl = conversationOperator.thumbUrl;
    } else {
        conversation.userFirstName = 'Unknown';
        conversation.userLastName = 'User';
        conversation.userThumbUrl = null;
    }
}

function* fillMessageContactInfo(message, members) {
    if (members === undefined) {
        const company = yield select(getCompany);
        // eslint-disable-next-line prefer-destructuring
        members = company.members;
    }

    if (message.from && message.from.platform === config.platformsMap.company) {
        const fromOperator = members.find(curOperator => curOperator.id === message.from.platformId);
        Object.assign(message.from, fromOperator);
    }

    if (message.to && message.to.platform === config.platformsMap.company) {
        const toOperator = members.find(curOperator => curOperator.id === message.from.platformId);
        Object.assign(message.to, toOperator);
    }
}

function* loadOlderMessages() {
    try {
        const chat = yield select(getChat);
        const company = yield select(getCompany);
        const {members} = company;

        const {conversation: {messages, id}} = chat;
        const offset = messages.length;
        const {messages: newLoadedMessages} = yield call(fetchConversationMessages, id, offset);

        for (const message of newLoadedMessages) {
            yield call(fillMessageContactInfo, message, members);
        }
        yield put(loadOlderMessagesSucceeded(newLoadedMessages));
    } catch (e) {
        yield put(loadOlderMessagesFailed());
    }
}


// WEBPACK FOOTER //
// ./src/services/chat/saga.js